import { useEffect, useState } from "react";

const PHASES = [
  "Connecting to your school",
  "Loading fee structures",
  "Reconciling payments",
  "Building your console",
];

export function DashboardSkeleton({ label }: { label?: string }) {
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const phaseTimer = setInterval(() => {
      setPhase((p) => (p + 1) % PHASES.length);
    }, 1400);
    const progTimer = setInterval(() => {
      setProgress((p) => Math.min(95, p + Math.random() * 8));
    }, 200);
    return () => {
      clearInterval(phaseTimer);
      clearInterval(progTimer);
    };
  }, []);

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        {/* Animated BRECCS emblem */}
        <div className="relative mx-auto mb-8 h-32 w-32">
          {/* Outer rotating ring */}
          <svg className="absolute inset-0 h-full w-full animate-spin-slow" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="54" fill="none" stroke="#b87333" strokeWidth="1.5" strokeDasharray="4 8" opacity="0.4" />
          </svg>
          {/* Middle counter-rotating ring */}
          <svg className="absolute inset-2 h-[calc(100%-1rem)] w-[calc(100%-1rem)] animate-spin-reverse" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="44" fill="none" stroke="#0f3d3e" strokeWidth="2" strokeDasharray="8 4" opacity="0.3" />
          </svg>
          {/* Progress arc */}
          <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="48" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="4" />
            <circle
              cx="60" cy="60" r="48" fill="none" stroke="#0f3d3e" strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 48}`}
              strokeDashoffset={`${2 * Math.PI * 48 * (1 - progress / 100)}`}
              className="transition-all duration-300 ease-out"
            />
          </svg>
          {/* Center pulse */}
          <div className="absolute inset-0 grid place-items-center">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-[#0f3d3e] shadow-lg">
              <div className="font-display text-xl font-bold text-[#d4a574]">B</div>
            </div>
          </div>
          {/* Floating dots */}
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="absolute h-2 w-2 rounded-full bg-[#b87333]/40"
              style={{
                top: `${50 + 42 * Math.sin((phase + i) * Math.PI / 2)}%`,
                left: `${50 + 42 * Math.cos((phase + i) * Math.PI / 2)}%`,
                transform: "translate(-50%, -50%)",
                transition: "all 0.8s ease-in-out",
                opacity: 0.3 + i * 0.15,
              }}
            />
          ))}
        </div>

        {/* Phase text with morph */}
        <div className="relative h-6 overflow-hidden">
          {PHASES.map((p, i) => (
            <div
              key={i}
              className="absolute inset-0 text-sm font-medium text-muted-foreground transition-all duration-500"
              style={{
                transform: `translateY(${(i - phase) * 100}%)`,
                opacity: i === phase ? 1 : 0,
              }}
            >
              {p}
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#0f3d3e] to-[#b87333] transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Percentage */}
        <div className="mt-2 font-mono text-xs text-muted-foreground">{Math.round(progress)}%</div>

        {/* Shimmer skeleton blocks */}
        <div className="mt-8 space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-3 rounded-full bg-muted/50 overflow-hidden"
              style={{ width: `${80 - i * 15}%`, margin: "0 auto" }}
            >
              <div className="h-full w-full animate-shimmer bg-gradient-to-r from-transparent via-muted-foreground/10 to-transparent" />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
        .animate-spin-reverse { animation: spin-reverse 6s linear infinite; }
        .animate-shimmer { animation: shimmer 1.8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
